import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  LEADERBOARD_CATEGORIES,
  LEADERBOARD_PERIODS,
  fetchLeaderboardCourses,
  fetchLmsLeaderboard,
  formatLeaderboardValue,
} from '../../services/lmsLeaderboardApi';

const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];
const RANK_EMOJI = ['🥇', '🥈', '🥉'];

const parseStudentData = () => {
  try {
    const raw = localStorage.getItem('student_data');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function LmsLeaderboardPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const student = parseStudentData();
  const currentPhone = student?.phone;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseCode, setCourseCode] = useState('');
  const [category, setCategory] = useState('overall');
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);

  const loadCourses = useCallback(async () => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }
    try {
      const res = await fetchLeaderboardCourses();
      const list = res.courses || [];
      setCourses(list);
      if (list.length > 0) {
        const enrolled = student?.enrolled_courses || [];
        const preferred = list.find((c) =>
          enrolled.some((e) => String(e).toUpperCase() === c.courseCode),
        );
        setCourseCode(preferred?.courseCode || list[0].courseCode);
      }
    } catch (err) {
      console.error(err);
      const fallback = (student?.enrolled_courses || []).map((code) => ({
        courseCode: String(code).toUpperCase(),
        courseName: String(code).toUpperCase(),
      }));
      setCourses(fallback);
      if (fallback.length > 0) setCourseCode(fallback[0].courseCode);
    }
  }, [navigate, student?.enrolled_courses]);

  const loadLeaderboard = useCallback(async () => {
    if (!courseCode) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchLmsLeaderboard(courseCode, { category, period });
      if (res.success) {
        setData(res);
      } else {
        setError(res.message || 'Failed to load leaderboard');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load leaderboard. Try again.');
    } finally {
      setLoading(false);
    }
  }, [courseCode, category, period]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    if (courseCode) loadLeaderboard();
  }, [courseCode, loadLeaderboard]);

  const activeMeta = LEADERBOARD_CATEGORIES.find((c) => c.id === category);
  const entries = data?.entries || [];
  const topThree = entries.slice(0, 3);
  const rest = entries.slice(3);
  const myRank = data?.studentRank;

  const isMe = (phone) => phone === currentPhone;

  const renderPodiumCard = (entry, index) => {
    if (!entry) return null;
    const formatted = formatLeaderboardValue(category, entry.primaryValue, entry.secondaryValue);
    const color = RANK_COLORS[index] || '#94A3B8';

    return (
      <Card
        key={entry.studentId || entry.phone}
        sx={{
          flex: 1,
          minWidth: isMobile ? '100%' : 0,
          background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
          border: `2px solid ${color}`,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography sx={{ fontSize: 32, mb: 1 }}>{RANK_EMOJI[index]}</Typography>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 1,
              bgcolor: color,
              fontWeight: 'bold',
            }}
          >
            {entry.studentName?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {entry.studentName}
            {isMe(entry.phone) && (
              <StarIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle', color: '#6366F1' }} />
            )}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mt: 1 }}>
            {formatted.primary}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatted.secondary}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', pb: 4 }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
          color: 'white',
          py: 2,
          mb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <EmojiEventsIcon sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                LMS Leaderboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Course-wise rankings — compete across videos, quizzes, games & more
              </Typography>
            </Box>
            <IconButton onClick={loadLeaderboard} sx={{ color: 'white' }} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Course</InputLabel>
              <Select
                value={courseCode}
                label="Course"
                onChange={(e) => setCourseCode(e.target.value)}
              >
                {courses.map((c) => (
                  <MenuItem key={c.courseCode} value={c.courseCode}>
                    {c.courseName} ({c.courseCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Period</InputLabel>
              <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
                {LEADERBOARD_PERIODS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {data?.courseName && (
              <Chip
                label={`${data.courseName} · ${data.totalParticipants || 0} active`}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Paper>

        <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={category}
            onChange={(_, v) => setCategory(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}
          >
            {LEADERBOARD_CATEGORIES.map((cat) => (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{cat.icon}</span>
                    <span>{isMobile ? cat.label.split(' ')[0] : cat.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {activeMeta && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <strong>{activeMeta.icon} {activeMeta.label}</strong>
            {' — '}
            {data?.categoryMeta?.description ||
              'Top performers in this category for the selected period.'}
          </Alert>
        )}

        {myRank?.rank > 0 && (
          <Card
            sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              color: 'white',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" fontWeight="bold">
                #{myRank.rank}
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Your rank · {myRank.totalStudents} students
                </Typography>
                <Typography fontWeight="bold">
                  {formatLeaderboardValue(category, myRank.primaryValue, myRank.secondaryValue).primary}
                  {' · '}
                  {formatLeaderboardValue(category, myRank.primaryValue, myRank.secondaryValue).secondary}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && entries.length === 0 && (
          <Card sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              No activity yet
            </Typography>
            <Typography color="text.secondary">
              Start learning, play games, or take quizzes to appear on the leaderboard!
            </Typography>
          </Card>
        )}

        {!loading && topThree.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              mb: 3,
              alignItems: 'stretch',
            }}
          >
            {!isMobile && renderPodiumCard(topThree[1], 1)}
            {renderPodiumCard(topThree[0], 0)}
            {!isMobile && renderPodiumCard(topThree[2], 2)}
            {isMobile && (
              <>
                {topThree[1] && renderPodiumCard(topThree[1], 1)}
                {topThree[2] && renderPodiumCard(topThree[2], 2)}
              </>
            )}
          </Box>
        )}

        {!loading && rest.length > 0 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                All Rankings
              </Typography>
              {rest.map((entry) => {
                const formatted = formatLeaderboardValue(
                  category,
                  entry.primaryValue,
                  entry.secondaryValue,
                );
                return (
                  <Paper
                    key={entry.studentId || entry.phone}
                    elevation={isMe(entry.phone) ? 3 : 0}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: 2,
                      border: isMe(entry.phone) ? '2px solid #6366F1' : '1px solid #e5e7eb',
                      bgcolor: isMe(entry.phone) ? '#f5f3ff' : 'white',
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 36,
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}
                    >
                      #{entry.rank}
                    </Typography>
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {entry.studentName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight="bold" noWrap>
                        {entry.studentName}
                        {isMe(entry.phone) && ' (You)'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatted.primary} · {formatted.secondary}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
}
